import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { SOLUTIONS } from "@/lib/marketing";
import { PublicShell, Crumbs } from "@/components/PublicShell";

export default function Solution() {
  const { slug } = useParams();
  const s = SOLUTIONS[slug];
  useSeo({
    title: s ? `${s.title} | Unity & Utility` : "Unity & Utility",
    description: s?.lead,
    path: `/solutions/${slug}`,
    jsonLd: s ? {
      "@context": "https://schema.org",
      "@graph": [
        breadcrumbs([["Home", "/"], ["What it does", "/solutions/inventory"], [s.h1, `/solutions/${slug}`]]),
        {
          "@type": "FAQPage",
          mainEntity: s.faqs.map(([q, a]) => ({
            "@type": "Question", name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        },
      ],
    } : null,
  });

  if (!s) return <Navigate to="/" replace />;

  return (
    <PublicShell>
      <article className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid={`solution-${slug}`}>
        <Crumbs items={[["Home", "/"], [s.h1, null]]} />
        <p className="overline text-[#002FA7]">{s.intent}</p>
        <h1 data-testid="solution-h1" className="font-display mt-3 text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl">
          {s.h1}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-600">{s.lead}</p>

        <div className="mt-8 space-y-3" data-testid="solution-points">
          {s.points.map(([head, body]) => (
            <div key={head} className="border-l-2 border-[#002FA7] bg-white py-3 pl-4 pr-4">
              <div className="flex items-start gap-2">
                <CheckCircle size={16} className="mt-0.5 shrink-0 text-[#002FA7]" />
                <div>
                  <div className="text-sm font-semibold text-[#0F172A]">{head}</div>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {s.tool && (
          <Link to={s.tool[1]} data-testid="solution-tool-cta" className="mt-8 inline-flex items-center gap-2 bg-[#002FA7] px-5 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#00248A]">
            {s.tool[0]} <ArrowRight size={15} />
          </Link>
        )}

        <h2 className="mt-12 text-base font-semibold text-[#0F172A] md:text-lg">Questions people ask</h2>
        <dl className="mt-3 space-y-4" data-testid="solution-faqs">
          {s.faqs.map(([q, a]) => (
            <div key={q} className="border border-border bg-white p-4">
              <dt className="text-sm font-semibold text-[#0F172A]">{q}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-slate-600">{a}</dd>
            </div>
          ))}
        </dl>

        <h2 className="mt-12 text-base font-semibold text-[#0F172A] md:text-lg">Next</h2>
        <ul className="mt-3 grid gap-2 sm:grid-cols-3" data-testid="solution-related">
          {s.related.map((other) => (
            <li key={other}>
              <Link to={`/solutions/${other}`} data-testid={`solution-related-${other}`} className="block border border-border bg-white px-4 py-3 text-sm text-slate-700 transition-colors duration-150 hover:border-[#002FA7] hover:text-[#002FA7]">
                {SOLUTIONS[other].h1}
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-12 border border-border bg-white p-6">
          <p className="text-sm text-slate-600">Free to start. No card needed to look around.</p>
          <Link to="/register" data-testid="solution-cta-register" className="mt-3 inline-flex items-center gap-2 bg-[#002FA7] px-5 py-3 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#00248A]">
            Create an account <ArrowRight size={15} />
          </Link>
        </div>
      </article>
    </PublicShell>
  );
}
